import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
// Icons
import { Plus, Trash2, Pencil, Loader2 } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { Link, useNavigate } from "react-router-dom";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
// Formik
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { apiErrorMessage } from "Common/constants/apiErrors";
import useServerSorting from "Common/hooks/useServerSorting";

// ========== آدرس پایه API ==========
const BASE_URL = "https://portal-api.mahanpanel.com";

// ========== اینترفیس‌ها ==========
interface Currency {
  id: number;
  name: string;
  created_at?: string;
  created_at_jalali?: string;
  updated_at?: string;
  updated_at_jalali?: string;
}

// ========== تابع کمکی برای اطمینان از آرایه ==========
const ensureArray = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "object") {
    const keys = ["items", "data", "results", "list"];
    for (const key of keys) {
      if (data[key] && Array.isArray(data[key])) {
        return data[key];
      }
    }
    const values = Object.values(data);
    if (values.length === 1 && Array.isArray(values[0])) {
      return values[0];
    }
    return values;
  }
  return [];
};

// ========== کامپوننت اصلی ==========
const Index = () => {
  const navigate = useNavigate();

  // State داده‌ها
  const [data, setData] = useState<Currency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);
  const sorting = useServerSorting();

  // Modal states
  const [show, setShow] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [eventData, setEventData] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // ========== دریافت لیست ارزها ==========
  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      if (!token) {
        navigate("/login");
        return;
      }

      const res = await axios.get(`${BASE_URL}/currencies/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: sorting.params,
      });

      const items = ensureArray(res.data);
      setData(items);
    } catch (error: any) {
      console.error("Error fetching currencies:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      toast.error("مشکلی در دریافت لیست ارزها رخ داده است.");
    } finally {
      setLoading(false);
    }
  }, [navigate, sorting.sortBy, sorting.sortOrder]);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies, reload]);

  // ========== حذف ارز ==========
  const handleDelete = async () => {
    if (!eventData) return;

    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      if (!token) {
        navigate("/login");
        return;
      }

      const res = await axios.delete(`${BASE_URL}/currencies/${eventData}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 200) {
        toast.success("ارز با موفقیت حذف شد");
        setReload(!reload);
        setDeleteModal(false);
        setEventData(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 404) {
        toast.error("ارز مورد نظر یافت نشد");
      } else {
        toast.error(apiErrorMessage(error, "خطا در حذف ارز"));
      }
    }
  };

  const onClickDelete = (id: number) => {
    setEventData(id);
    setDeleteModal(true);
  };

  const deleteToggle = () => setDeleteModal(!deleteModal);

  // ========== ثبت ارز جدید ==========
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("لطفاً نام ارز را وارد کنید")
        .min(2, "نام ارز حداقل ۲ کاراکتر باید باشد"),
    }),
    onSubmit: async (values) => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        if (!token) {
          navigate("/login");
          return;
        }

        if (editMode && editingId) {
          const res = await axios.patch(
            `${BASE_URL}/currencies/${editingId}`,
            { name: values.name },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 200) {
            toast.success(`ارز "${values.name}" با موفقیت ویرایش شد`);
            setReload(!reload);
            toggle();
          }
        } else {
          const res = await axios.post(
            `${BASE_URL}/currencies/`,
            { name: values.name },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (res.status === 201) {
            toast.success(`ارز "${values.name}" با موفقیت ثبت شد`);
            setReload(!reload);
            toggle();
          }
        }
      } catch (error: any) {
        console.error("Submit error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 422) {
          // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
          // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
          let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          toast.error(msg);
        } else {
          toast.error(error.response?.data?.detail || (editMode ? "خطا در ویرایش ارز" : "خطا در ثبت ارز"));
        }
      }
    },
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEditMode(false);
      setEditingId(null);
      validation.resetForm();
    } else {
      setShow(true);
      validation.resetForm();
    }
  }, [show, validation]);

  const openCreateModal = () => {
    setEditMode(false);
    setEditingId(null);
    validation.resetForm();
    setShow(true);
  };

  const handleEdit = (row: Currency) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({ name: row.name || "" });
    setShow(true);
  };

  // ========== ستون‌های جدول ==========
  const columns = useMemo(
    () => [
      {
        header: "شناسه",
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (cell: any) => <span>{cell.getValue()}</span>,
      },
      {
        header: "نام ارز",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <Link
            to="#!"
            className="transition-all duration-150 ease-linear text-custom-500 hover:text-custom-600"
          >
            {cell.getValue()}
          </Link>
        ),
      },
      {
        header: "عملیات",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const row: Currency = cell.row.original;
          return (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleEdit(row)}
                className="text-blue-500 hover:text-blue-700 transition-colors"
              >
                <Pencil className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => onClickDelete(row.id)}
                className="text-red-500 hover:text-red-700 transition-colors"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="ثبت ارز" title="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
      <ToastContainer
        position="top-center"
        closeButton={false}
        limit={1}
        rtl={true}
        className="font-Bold text-lg"
      />
      <div className="card" id="currencyTable">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع ارزها (<b className="total-Employs">{data?.length}</b>)
            </h6>
            <div className="shrink-0">
              <button
                onClick={openCreateModal}
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
              >
                <span className="align-middle rtl">ثبت ارز جدید</span>{" "}
                <Plus className="inline-block size-4 rtl" />
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data || []}
              serverSorting={{ sortBy: sorting.sortBy, sortOrder: sorting.sortOrder, onChange: sorting.onChange }}
              customPageSize={5}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          )}
        </div>
      </div>

      {/* ========== مودال ثبت ارز جدید ========== */}
      <Modal
        show={show}
        onHide={toggle}
        modal-center="true"
        dialogClassName="w-screen md:w-[30rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">{editMode ? "ویرایش ارز" : "اضافه کردن ارز"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form
            className="create-form"
            id="create-form"
            onSubmit={validation.handleSubmit}
          >
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-11 dark:text-white text-gray-800">
                <div className="flex items-center">
                  <label
                    htmlFor="name"
                    className="text-base font-medium ml-2 w-1/4"
                  >
                    نام ارز:
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="form-input font-[12px] w-full"
                    placeholder="مثال: ریال، دلار، یورو"
                    name="name"
                    autoComplete="off"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name}
                  />
                </div>
                {validation.touched.name && validation.errors.name && (
                  <p className="text-red-400 mr-24 mt-2">
                    {validation.errors.name}
                  </p>
                )}
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleClick}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
          >
            {editMode ? "ذخیره تغییرات" : "ثبت"}
          </button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
